import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  Email,
  CheckCircle,
  Visibility,
  TouchApp,
  ErrorOutline,
  Report,
  Unsubscribe,
  AutoGraph,
} from '@mui/icons-material';
import type { CampaignStats, CampaignStatsProvider, ProviderCampaignStats } from '../../services/outreach/types';
import { themeColors } from '../../utils/theme';
import { useCampaignStats } from '../../hooks/queries';
import { formatDistanceToNow } from 'date-fns';

const formatCount = (num?: number): string => {
  if (typeof num !== 'number' || Number.isNaN(num)) {
    return '0';
  }
  return Math.round(num).toLocaleString();
};

const formatPercent = (num?: number): string => {
  if (typeof num !== 'number' || Number.isNaN(num)) {
    return '0.0%';
  }
  return `${num.toFixed(1)}%`;
};

const calculateEngagementRate = (opened: number, clicked: number, delivered: number): number => {
  if (delivered <= 0) {
    return 0;
  }
  return ((opened + clicked) / delivered) * 100;
};

const calculateClickToOpenRate = (opened: number, clicked: number): number => {
  if (opened <= 0) {
    return 0;
  }
  return (clicked / opened) * 100;
};

const PROVIDER_LABELS: Record<CampaignStatsProvider, string> = {
  ses: 'SES',
  mailgun: 'Mailgun',
};

const PROVIDER_CHIP_COLORS: Record<CampaignStatsProvider, 'success'> = {
  ses: 'success',
  mailgun: 'success',
};

const SES_REFRESH_INTERVAL_MS = 15 * 1000;

interface CampaignStatisticsProps {
  campaignId: string;
}

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = themeColors.primary,
  trend,
}) => {
  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${themeColors.paper} 0%, ${color}10 100%)`,
        border: `1px solid ${color}20`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 25px ${color}20`,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: `${color}20`,
              color: color,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {value}
            </Typography>
          </Box>
        </Box>
        
        {subtitle && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TrendingUp
              sx={{
                fontSize: 16,
                color: trend.isPositive ? '#4caf50' : '#f44336',
                transform: trend.isPositive ? 'none' : 'rotate(180deg)',
                mr: 0.5,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: trend.isPositive ? '#4caf50' : '#f44336',
                fontWeight: 'medium',
              }}
            >
              {trend.isPositive ? '+' : ''}{trend.value}% vs previous
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const CampaignStatistics: React.FC<CampaignStatisticsProps> = ({ campaignId }) => {
  const provider: CampaignStatsProvider = 'ses';
  const autoRefreshInterval = SES_REFRESH_INTERVAL_MS;

  // Use React Query hook for campaign statistics with provider filtering
  const {
    data: stats,
    isLoading,
    error,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useCampaignStats(campaignId, {
    provider,
    refetchInterval: autoRefreshInterval,
    staleTime: autoRefreshInterval,
  });

  const activeStats = useMemo<CampaignStats | null>(() => {
    if (!stats) {
      return null;
    }

    return stats;
  }, [stats, provider]);

  const lastUpdatedTimestamp =
    activeStats?.lastUpdated ??
    (dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : undefined);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdatedTimestamp) {
      return null;
    }
    const parsed = new Date(lastUpdatedTimestamp);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return formatDistanceToNow(parsed, { addSuffix: true });
  }, [lastUpdatedTimestamp]);

  const providerSummaries = useMemo(() => {
    if (!stats?.providers?.ses) {
      return [];
    }

    const sesProvider = stats.providers.ses;
    return [{
      provider: 'ses' as CampaignStatsProvider,
      deliveryRate: formatPercent(sesProvider.deliveryRate),
      bounceRate: formatPercent(sesProvider.bounceRate),
      totalSent: formatCount(sesProvider.totalSent),
    }];
  }, [stats]);

  const engagementRate = activeStats
    ? typeof activeStats.engagementRate === 'number'
      ? activeStats.engagementRate
      : calculateEngagementRate(activeStats.opened, activeStats.clicked, activeStats.delivered)
    : 0;

  const clickToOpenRate = activeStats
    ? typeof activeStats.clickToOpenRate === 'number'
      ? activeStats.clickToOpenRate
      : calculateClickToOpenRate(activeStats.opened, activeStats.clicked)
    : 0;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          p: 3,
        }}
      >
        <CircularProgress size={48} sx={{ color: themeColors.primary, mb: 2 }} />
        <Typography variant="body1" color="textSecondary">
          Loading campaign statistics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          mb: 2,
          backgroundColor: `${themeColors.paper}`,
          border: '1px solid #f44336',
        }}
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        <Typography variant="body2">
          {error instanceof Error ? error.message : 'Failed to load campaign statistics'}
        </Typography>
      </Alert>
    );
  }

  if (!activeStats) {
    return (
      <Alert
        severity="info"
        sx={{
          mb: 2,
          backgroundColor: `${themeColors.paper}`,
          border: `1px solid ${themeColors.primary}`,
        }}
      >
        <Typography variant="body2">
          No statistics available for the {PROVIDER_LABELS[provider].toLowerCase()} provider.
        </Typography>
      </Alert>
    );
  }

  const viewingLabel = PROVIDER_LABELS[provider];
  const autoRefreshLabel = 'every 15s';

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: themeColors.text }}>
            Campaign Statistics
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Performance metrics for campaign: {campaignId}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Chip
              label={`Viewing ${viewingLabel}`}
              color={PROVIDER_CHIP_COLORS[provider]}
              size="small"
              variant="filled"
            />
            <Chip label={`Auto-refresh ${autoRefreshLabel}`} size="small" variant="outlined" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isFetching && !isLoading && (
                <CircularProgress size={14} sx={{ color: themeColors.primary }} />
              )}
              {lastUpdatedLabel && (
                <Typography variant="caption" color="textSecondary">
                  Updated {lastUpdatedLabel}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main Statistics Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Sent */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Sent"
            value={formatCount(activeStats.totalSent)}
            subtitle="Messages sent"
            icon={<Email />}
            color={themeColors.primary}
          />
        </Grid>

        {/* Delivery Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Delivery Rate"
            value={formatPercent(activeStats.deliveryRate)}
            subtitle={`${formatCount(activeStats.delivered)} delivered`}
            icon={<CheckCircle />}
            color="#4caf50"
          />
        </Grid>

        {/* Open Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Open Rate"
            value={formatPercent(activeStats.openRate)}
            subtitle={`${formatCount(activeStats.opened)} opened`}
            icon={<Visibility />}
            color="#2196f3"
          />
        </Grid>

        {/* Click Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Click Rate"
            value={formatPercent(activeStats.clickRate)}
            subtitle={`${formatCount(activeStats.clicked)} clicked`}
            icon={<TouchApp />}
            color="#ff9800"
          />
        </Grid>
      </Grid>

      {/* Secondary Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Bounce Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Bounce Rate"
            value={formatPercent(activeStats.bounceRate)}
            subtitle={`${formatCount(activeStats.bounced)} bounced`}
            icon={<ErrorOutline />}
            color="#f44336"
          />
        </Grid>

        {/* Complaint Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Complaint Rate"
            value={formatPercent(activeStats.complaintRate)}
            subtitle={`${formatCount(activeStats.complained)} complaints`}
            icon={<Report />}
            color="#e91e63"
          />
        </Grid>

        {/* Unsubscribe Count */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unsubscribed"
            value={formatCount(activeStats.unsubscribed)}
            subtitle="Recipients unsubscribed"
            icon={<Unsubscribe />}
            color="#9c27b0"
          />
        </Grid>

        {/* Engagement Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Engagement Rate"
            value={formatPercent(engagementRate)}
            subtitle="Opens + clicks / delivered"
            icon={<TrendingUp />}
            color="#00bcd4"
          />
        </Grid>

        {/* Click-to-Open Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Click-to-Open Rate"
            value={formatPercent(clickToOpenRate)}
            subtitle="Clicks / opens"
            icon={<AutoGraph />}
            color="#673ab7"
          />
        </Grid>
      </Grid>

      {/* Summary Card */}
      <Paper
        sx={{
          p: 3,
          backgroundColor: themeColors.paper,
          border: `1px solid ${themeColors.primary}20`,
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ color: themeColors.primary }}>
          Campaign Summary
        </Typography>
        
        <Divider sx={{ my: 2, borderColor: `${themeColors.primary}30` }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Performance Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${formatPercent(activeStats.deliveryRate)} Delivered`}
                  color={
                    activeStats.deliveryRate >= 95
                      ? 'success'
                      : activeStats.deliveryRate >= 85
                        ? 'warning'
                        : 'error'
                  }
                  size="small"
                />
                <Chip
                  label={`${formatPercent(activeStats.openRate)} Opened`}
                  color={
                    activeStats.openRate >= 20
                      ? 'success'
                      : activeStats.openRate >= 10
                        ? 'warning'
                        : 'error'
                  }
                  size="small"
                />
                <Chip
                  label={`${formatPercent(activeStats.clickRate)} Clicked`}
                  color={
                    activeStats.clickRate >= 3
                      ? 'success'
                      : activeStats.clickRate >= 1
                        ? 'warning'
                        : 'error'
                  }
                  size="small"
                />
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Key Metrics
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Total Recipients: <strong>{formatCount(activeStats.totalSent)}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Successfully Delivered: <strong>{formatCount(activeStats.delivered)}</strong>
              </Typography>
              <Typography variant="body2">
                • Overall Engagement: <strong>{formatPercent(engagementRate)}</strong>
              </Typography>
              <Typography variant="body2">
                • Click-to-Open: <strong>{formatPercent(clickToOpenRate)}</strong>
              </Typography>
              {providerSummaries.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Provider Breakdown
                  </Typography>
                  {providerSummaries.map((summary) => (
                    <Typography key={summary.provider} variant="body2" sx={{ mb: 0.5 }}>
                      • {PROVIDER_LABELS[summary.provider]}: <strong>{summary.deliveryRate}</strong> delivery rate,{' '}
                      <strong>{summary.bounceRate}</strong> bounce rate ({summary.totalSent} sent)
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CampaignStatistics;
